import { IRawStyle, memoizeFunction, mergeStyles } from '@fluentui/react';

import { ribbonElementTextContainerSelector } from '@1js/acui-common';
import { MenuDefinition, MenuSectionGeneric, SimpleMenuItemControlProps } from '@1js/acui-menu';

import {
  estimateRibbonScalingStepsSLR,
  getControlGroupFromScalingStep,
  getCoreRibbonElementForGalleriesConvertedToFlyoutAnchorsSelector,
  getCoreRibbonElementForOverflowSelector,
  getCoreRibbonElementSelector,
  getMoveToOverflowSelector,
  getRibbonBottomBarWidthSLR,
  initializeVirtualScalingStateSLR,
} from '.';
import {
  BuildTimeControlWidths,
  EstimatedScalingWidthDict,
  hiddenControlStyle,
  makeGhostFlyoutVisibleStyle,
  makeOverflowVisibleStyle,
  RibbonScalingStateSLRWithCacheKeys,
  ScalingStepsStyle,
  Selectors,
  WithSectionId,
} from '..';
import {
  MoveToOverflow,
  RibbonControlScalingTransformation,
  RibbonGroupScalingTransformation,
  RibbonScalingStep,
  RibbonScalingTransformation,
  singleMoveToOverflowScalingClassNameIndex,
  singleRibbonOverflowMenuId,
} from '../../../Scaling';
import { MoveGroupToSingleOverflow } from '../../../Scaling/MoveGroupToSingleOverflow';
import { chevronWidth, tabContentContainerPadding } from '../RibbonBottomBar.classNames';

/**
 * Returns a simple cache object is able to store classNames with a given key.
 */
const classNameCache: { [key: string]: string } = {};
const getClassNameCache = () => {
  return {
    /**
     * Checks if the provided data has a cacheKey. If it has a cacheKey and there is a
     * corresponding entry in the classNameCache, then it will return that value.
     * Returns undefined otherwise.
     */
    getCachedClassName: (data: RibbonScalingStateSLRWithCacheKeys): string | undefined => {
      return data && data.cacheKey && classNameCache[data.cacheKey];
    },
    /**
     * Should be called whenever there is a new measurement associated with a given data object.
     * If the data has a cacheKey, store that measurement in the measurementsCache.
     */
    addMeasurementToCache: (data: RibbonScalingStateSLRWithCacheKeys, className: string): void => {
      if (data.cacheKey) {
        classNameCache[data.cacheKey] = className;
      }
    },
  };
};

let estimatedWidths: EstimatedScalingWidthDict = {};
let lastKnownScalingState: RibbonScalingStateSLRWithCacheKeys | undefined = undefined;
let lastKnownWidths: BuildTimeControlWidths | undefined = undefined;
let lastKnownMergedClassName: string | undefined = undefined;
let galleriesConvertedToFlyouts: { [galleryId: string]: boolean } = {};
let dropPreviewGalleryItemIndexByControlId: { [id: string]: number } = {};
let controlGroupsWithOverflowingContent: string[] = [];

/**
 * Gets a className that corresponds to the given scaling state
 * @param scalingState The scaling state used to build the className
 * @param knownWidths The known control widths used to help calculate the className
 * @param reactMLRIsEnabled Is the react MLR enabled?
 * @param forceRecalc Should we force a recalc? This can be true if we know we need to calculate a new width
 * @returns A className for the given scaling state
 */
export function getScalingClassName(
  scalingState: RibbonScalingStateSLRWithCacheKeys,
  knownWidths: BuildTimeControlWidths,
  reactMLRIsEnabled: boolean,
  forceRecalc: boolean
) {
  // If we are not forced to recalc, we have known mergedClassName (we've calculated something before),
  // and our known widths are the same; we can potentially return a cached className
  if (!forceRecalc && lastKnownMergedClassName && JSON.stringify(knownWidths) === JSON.stringify(lastKnownWidths)) {
    // If we have the previous scaling state and the cache keys are the same, return the
    // previously computed className
    if (lastKnownScalingState && lastKnownScalingState.cacheKey === scalingState.cacheKey) {
      return lastKnownMergedClassName;
    }

    // The scaling state is different from the previous call, but we may have already
    // computed the className and stored it in the cache. if so, return the cached className
    const cachedClassName = getClassNameCache().getCachedClassName(scalingState);
    if (cachedClassName) {
      lastKnownScalingState = scalingState;
      return cachedClassName;
    }
  }

  // We have not computed the styling for this scaling state,
  // set up variables to build new styling
  galleriesConvertedToFlyouts = {};
  dropPreviewGalleryItemIndexByControlId = {};
  lastKnownScalingState = scalingState;
  let style: ScalingStepsStyle = { selectors: {} };
  controlGroupsWithOverflowingContent = [];

  // Add the styles for the initial control scaling steps
  addInitialScalingStepsStyles(scalingState, style);

  // Update the estimated width of the ribbon for each scaling step
  updateEstimatedRibbonScalingStepWidths(scalingState, knownWidths, reactMLRIsEnabled);

  // Add the initial overflow styles
  addInitialOverflowStyles(scalingState, style);

  // Add the styles for the scaling steps. This will add the styles for the non-initial scaling steps
  addScalingStepsStyles(scalingState, style);

  const mergedStylesClassName = mergeStyles(style);
  getClassNameCache().addMeasurementToCache(scalingState, mergedStylesClassName);
  lastKnownMergedClassName = mergedStylesClassName;

  return mergedStylesClassName;
}

/**
 * Adds the styles for applying the initial scaling steps
 * Note, these scaling steps are always applied
 * @param scalingState The scaling state to build the styles for
 * @param style The style object to write the new scaling styles
 */
const addInitialScalingStepsStyles = (scalingState: RibbonScalingStateSLRWithCacheKeys, style: ScalingStepsStyle) => {
  // Loop over the InitialScalingSteps (zero to minimumScalingStepsApplied)
  for (
    let scalingStepIndex = 0;
    scalingStepIndex < scalingState.minimumScalingStepsToApply;
    scalingStepIndex = scalingStepIndex + 1
  ) {
    // Add the new selectors, these do not need a max-width because
    // they are initial scaling steps and hence are always applied
    style.selectors = {
      ...style.selectors,
      ...getScalingStepsStylesBase(scalingStepIndex, scalingState),
    };
  }
};

/**
 * Adds the styles for scaling the ribbon controls for the scaling state
 * based off of the estimated widths
 * @param scalingState The scaling state to build the styles for
 * @param style The style object to write the new scaling styles
 */
const addScalingStepsStyles = (scalingState: RibbonScalingStateSLRWithCacheKeys, style: ScalingStepsStyle) => {
  // Start at to one less than the minimum scaling step so that incrementing will work as expected
  let scalingIndex = scalingState.minimumScalingStepsToApply || 0;

  // Loop over the estimated widths to get the max-widths.
  // The first value should be the initial width (after the initialScalingSteps applied)
  for (let key in estimatedWidths) {
    const scalingIndexFromWidth = +key;
    // Add the selectors that make responsive scaling work
    style.selectors[`@media (max-width: ${estimatedWidths[key]}px)`] = {
      selectors: {
        ...((style.selectors[`@media (max-width: ${estimatedWidths[key]}px)`] as IRawStyle)?.selectors || {}),
        ...getScalingStepsStylesBase(scalingIndexFromWidth, scalingState),
      },
    };
    scalingIndex = scalingIndex + 1;
  }
};

/**
 * Base function to build the styles for the given scaling state
 * @param scalingStepIndex The top level scaling state index
 * @param scalingState The scaling state that should be processed
 * @returns a styles object for (selectors and style attributes) for the scaling step (the scaling step from the given scaling state at the given index)
 */
const getScalingStepsStylesBase = (scalingStepIndex: number, scalingState: RibbonScalingStateSLRWithCacheKeys) => {
  const selectors: Selectors = {};
  const scalingStep: RibbonScalingTransformation = scalingState.scalingSteps[
    scalingStepIndex
  ] as RibbonScalingTransformation;

  // If we don't get a scalingStep we cannot calculate the correct selectors, return early
  if (!scalingStep) {
    return selectors;
  }

  // Get core selector for the scaling step
  const selector: string = getCoreScalingStepSelector(scalingStep, scalingStepIndex);

  // Get additional selectors for gallery to flyout, if needed
  const makeGhostFlyoutVisibleSelector = getGhostFlyoutVisibleSelector(scalingStep);

  // Add overflow back if applicable
  const makeOverflowVisibleSelectors = getOverflowVisibleSelectors(scalingState, scalingStepIndex);

  if (selector) {
    selectors[selector] = hiddenControlStyle;
  }

  if (makeGhostFlyoutVisibleSelector) {
    selectors[makeGhostFlyoutVisibleSelector] = makeGhostFlyoutVisibleStyle;
  }

  if (makeOverflowVisibleSelectors) {
    selectors[makeOverflowVisibleSelectors] = makeOverflowVisibleStyle;
  }

  return selectors;
};

/**
 * Gets the selector for making the ghost flyout (that appears at the galleryToFlyout scaling step) visible
 * @param scalingStep The scaling step to check
 * @returns A selector for making the ghost flyout visible, if applicable, empty string otherwise
 */
const getGhostFlyoutVisibleSelector = (scalingStep: RibbonScalingTransformation) => {
  // Show the ghostFlyout if we are transitioning from gallery to flyout
  if (scalingStep.type === 'GalleryToFlyout') {
    return getCoreRibbonElementForGalleriesConvertedToFlyoutAnchorsSelector(scalingStep.controlId);
  }

  return '';
};

/**
 * This function builds up the selectors to make the overflows visible at the right time based on the scaling state
 * @param scalingState The scaling state
 * @param scalingStepIndex The scaling step index that should be processed
 * @param controlGroupsWithOverflowingContent An array to get and update the groups with overflowing content
 * @returns Selectors for when each overflow should be visible for the given scaling step index in the given scaling state
 */
const getOverflowVisibleSelectors = (scalingState: RibbonScalingStateSLRWithCacheKeys, scalingStepIndex: number) => {
  // Add overflow back if applicable
  let makeOverflowVisibleSelectors = '';

  // Make the current scaling step an array to align types for easier processing
  let currentScalingSteps: RibbonScalingTransformation[] = getScalingStepsAtIndexAsArray(
    scalingStepIndex,
    scalingState.scalingSteps
  );

  // Keep track of when we've set the single overflow to be visible
  let haveMadeSingleOverflowVisible = false;

  // Make overflow visible, if applicable
  for (let scalingStep of currentScalingSteps) {
    // If we are processing a moveToOverflow or moveGroupToOverflow, we may need to make the overflow visible if we have not already
    if (stepContainsToOverflow(scalingStep)) {
      // Get the control group for the scaling step
      const controlGroup = getControlGroupFromScalingStep(scalingState, scalingStep);

      // Check if this is the first moveToOverflow for this group, add the a selector to make the overflow visible
      if (controlGroup && addGroupWithOverflowIfFirstInstance(controlGroup.id)) {
        const existingVisibleSelectors = makeOverflowVisibleSelectors ? `${makeOverflowVisibleSelectors}, ` : '';

        // If we are dealing with a single overflow and are processing the first MoveToOverflow for this group,
        // make the single overflow visible, if needed, otherwise set make the group's overflow visible
        if (!controlGroup.hasOwnOverflow && scalingState.scaledEndOfRibbonOverflowMenu) {
          // If this is our first MoveToOverflow across groups, make the single overflow visible
          if (!haveMadeSingleOverflowVisible) {
            haveMadeSingleOverflowVisible = true;
            makeOverflowVisibleSelectors = `${existingVisibleSelectors}${getCoreRibbonElementForOverflowSelector(
              singleRibbonOverflowMenuId
            )}, ${getMoveToOverflowSelector(singleMoveToOverflowScalingClassNameIndex)}`;
          }
        } else {
          // We get here when we are processing the first moveToOverflow for a group (where the overflow is not going to a single overflow).
          // Make the group's overflow visible
          makeOverflowVisibleSelectors = `${existingVisibleSelectors}${getCoreRibbonElementForOverflowSelector(
            controlGroup.id
          )}`;
        }
      }
    }
  }

  return makeOverflowVisibleSelectors;
};

/**
 * Returns a selector based on the scaling index, the scaling steps, and the overall scaling index
 * @param scalingSteps The scaling step to build the selector from
 * @param topLevelScalingIndex The index in the top level set of scaling steps, used for indexing overflow selectors
 * @returns a selector for the given scaling step. This determines control, label, and overflow visibility
 */
const getCoreScalingStepSelector = memoizeFunction(
  (scalingStep: Readonly<RibbonScalingStep> | undefined, topLevelScalingIndex: number): string => {
    if (!scalingStep) {
      return '';
    }

    // Get the moveToOverflow selector for the current top level scaling index.
    // The vertical dividers also set the className this selector uses, when it needs to be dropped
    const moveToOverflowFullClassNameSelector = getMoveToOverflowSelector(topLevelScalingIndex);

    if (Array.isArray(scalingStep)) {
      // Build a selector comprised of all scaling steps in the array since they will all be scaled at the same time
      return getSelectorsForScalingSteps(scalingStep, topLevelScalingIndex, moveToOverflowFullClassNameSelector);
    }

    // Check if we are a control scaling transformation
    if (!Object(scalingStep).hasOwnProperty('controlId')) {
      // If we are moving group to single overflow, we need to use the groupId
      if ((scalingStep as RibbonGroupScalingTransformation).type === 'MoveGroupToSingleOverflow') {
        return getCoreRibbonElementForOverflowSelector((scalingStep as RibbonGroupScalingTransformation).groupId);
      }

      // We do not know what type of scaling transformation this is, return an empty string
      return '';
    }

    return getSelectorForScalingStepBasedOnType(scalingStep, moveToOverflowFullClassNameSelector);
  }
);

/**
 * Get a selector comprised of all scaling steps in the array
 * @param scalingSteps The scaling step to build the selector from
 * @param topLevelScalingIndex The scaling index in the top level scaling steps
 * @param moveToOverflowFullClassNameSelector The selector for the moveToOverflow scaling step
 * @returns A selector for the given scaling step array
 */
function getSelectorsForScalingSteps(
  scalingSteps: Readonly<RibbonScalingStep>[],
  topLevelScalingIndex: number,
  moveToOverflowFullClassNameSelector: string
) {
  // Combine the selectors for each step in the array
  let selectors: string = scalingSteps.reduce(
    (resultingSelectors, currentStep) =>
      `${resultingSelectors}, ${getCoreScalingStepSelector(currentStep, topLevelScalingIndex)}`,
    ''
  );

  // refine the scaling step selectors to remove duplication and the leading comma
  selectors = refineScalingStepSelectors(moveToOverflowFullClassNameSelector, selectors);

  return selectors;
}

/**
 * Gets a selector for the element in the given scaling step based on transformation type
 * @param scalingStep The scaling step used to build the selector
 * @param moveToOverflowFullClassNameSelector The MoveToOverflow selector for this scaling step
 * @returns A selector for the given scaling step or an empty string if do not know the scaling transformation
 */
function getSelectorForScalingStepBasedOnType(
  scalingStep: Readonly<RibbonScalingStep>,
  moveToOverflowFullClassNameSelector: string
) {
  const controlId = (scalingStep as RibbonControlScalingTransformation).controlId;

  const elementSelector = galleriesConvertedToFlyouts[controlId]
    ? getCoreRibbonElementForGalleriesConvertedToFlyoutAnchorsSelector(controlId)
    : getCoreRibbonElementSelector(controlId);

  // Build the selector for the given controlId for the give scaling step
  switch ((scalingStep as RibbonScalingTransformation).type) {
    case 'MoveToOverflow':
      return `${elementSelector}, ${moveToOverflowFullClassNameSelector}`;
    case 'DropLabel':
      return `${elementSelector} ${ribbonElementTextContainerSelector}`;
    case 'DropPreviewItemFromGallery':
      return `${elementSelector} ${getDropPreviewItemFromGalleryIndexSelector(controlId)}`;
    case 'GalleryToFlyout':
      galleriesConvertedToFlyouts[controlId] = true;
      return elementSelector;
    case 'MoveGroupToSingleOverflow':
      return getCoreRibbonElementForOverflowSelector((scalingStep as RibbonGroupScalingTransformation).groupId);
    default:
      return '';
  }
}

/**
 * Helper function to remove duplicate MoveToOverflow selectors as well as the leading ', '
 * @param moveToOverflowFullClassNameSelector The MoveToOverflow selector that need to be duplicated
 * @param selectors The selectors string to refine
 * @returns A refined version of the given selector
 */
function refineScalingStepSelectors(moveToOverflowFullClassNameSelector: string, selectors: string) {
  let refinedSelectors = selectors;

  // If we have moveToOverflow, make sure there's only one since they are all the same
  const moveToOverflowRegex = new RegExp(`, \\${moveToOverflowFullClassNameSelector}`, 'gi');
  if (moveToOverflowRegex.test(refinedSelectors)) {
    refinedSelectors = `${refinedSelectors.replace(moveToOverflowRegex, '')}, ${moveToOverflowFullClassNameSelector}`;
  }

  // Remove the extra leading ', ' since that will make this selector
  // match everything when given to mergeStyles!
  const leadingCommaRegex = new RegExp(`^, `, 'gi');
  if (leadingCommaRegex.test(refinedSelectors)) {
    refinedSelectors = refinedSelectors.replace(leadingCommaRegex, '');
  }

  return refinedSelectors;
}

/**
 * Add the initial overflow styles for both groups and the single overflow
 * @param scalingState The scaling state used to calculate the initial overflow styles
 * @param style The styled object where the initial overflow styles will be added
 * @param controlGroupsWithOverflowingContent An array to get and update the groups with overflowing content
 */
function addInitialOverflowStyles(scalingState: RibbonScalingStateSLRWithCacheKeys, style: ScalingStepsStyle) {
  const allGroupsHaveOwnOverflow = addInitialGroupOverflowStyles(scalingState, style);
  addInitialSingleOverflowStyle(scalingState, allGroupsHaveOwnOverflow, style);
}

function addInitialGroupOverflowStyles(scalingState: RibbonScalingStateSLRWithCacheKeys, style: ScalingStepsStyle) {
  let allGroupsHaveOwnOverflow = true;
  scalingState.scaledRibbonGroups.forEach(group => {
    allGroupsHaveOwnOverflow = allGroupsHaveOwnOverflow && !!group.hasOwnOverflow;
    if (!group.overflowControls || group.overflowControls.length === 0 || group.hasOwnOverflow === false) {
      style.selectors[getCoreRibbonElementForOverflowSelector(group.id)] = hiddenControlStyle;
    }
    if (group.overflowControls?.length > 0) {
      addGroupWithOverflowIfFirstInstance(group.id);
    }
  });
  return allGroupsHaveOwnOverflow;
}

/**
 * Adds the initial overflow styles to the style object for the given scaling state, if applicable
 * @param scalingState The scaling state to build the initial single overflow control styles
 * @param allGroupsHaveOwnOverflow Do all the groups of the scaling state have their own overflow?
 * @param style The style object to add the single overflow styles, if applicable
 * @returns
 */
function addInitialSingleOverflowStyle(
  scalingState: RibbonScalingStateSLRWithCacheKeys,
  allGroupsHaveOwnOverflow: boolean,
  style: ScalingStepsStyle
) {
  // If all groups have their own overflow or we do not have a single overflow menu,
  // we do not need to set a style for the initial overflow since there will never be a single overflow
  if (allGroupsHaveOwnOverflow || !scalingState.scaledEndOfRibbonOverflowMenu) {
    return;
  }

  // Get the index the the single oveflow becmoes visible
  const indexWhenSingleOverflowBecomesVisible = scalingState.scalingSteps.findIndex(scalingStep =>
    stepContainsToOverflow(scalingStep as RibbonScalingTransformation)
  );

  // If the single overflow is initially visible, we do not need to hide it, return early
  if (
    indexWhenSingleOverflowBecomesVisible >= 0 &&
    indexWhenSingleOverflowBecomesVisible < scalingState.minimumScalingStepsToApply
  ) {
    return;
  }

  // If the single overflow will be visible at some point, hide it initially
  if (indexWhenSingleOverflowBecomesVisible !== -1) {
    style.selectors[
      `${getCoreRibbonElementForOverflowSelector(singleRibbonOverflowMenuId)}, ${getMoveToOverflowSelector(
        singleMoveToOverflowScalingClassNameIndex
      )}`
    ] = hiddenControlStyle;
  }
}

/**
 * Gets a selector that references the preview gallery item that should be dropped.
 * Gallery preview items are always dropped from the end.
 * @param id The id of the preview gallery item that is being dropped
 * @returns A selector that references the preview gallery item that should be dropped
 */
function getDropPreviewItemFromGalleryIndexSelector(id: string) {
  if (dropPreviewGalleryItemIndexByControlId[id] === undefined) {
    dropPreviewGalleryItemIndexByControlId[id] = 1;
  }

  dropPreviewGalleryItemIndexByControlId[id] = dropPreviewGalleryItemIndexByControlId[id] + 1;

  // Build a selector to give the correct preview item
  // Note, we start from index two because we have to account for the preview expand button
  return `button:nth-last-child(${dropPreviewGalleryItemIndexByControlId[id]})`;
}

/**
 * Update the estimated ribbon widths based on the provided scaling steps. This function also
 * adds new control, previously unknown, widths to known widths
 * @param scalingState The scaling state to used to calculate the estimated ribbon widths per scaling step
 * @param knownWidths The known control widths
 * @param reactMLRIsEnabled Is the react mlr enabled
 */
function updateEstimatedRibbonScalingStepWidths(
  scalingState: RibbonScalingStateSLRWithCacheKeys,
  knownWidths: BuildTimeControlWidths,
  reactMLRIsEnabled: boolean
) {
  const scalingData = getRibbonBottomBarWidthSLR(
    scalingState,
    knownWidths,
    // account for bottomBar padding, the modeSwitcher, and add a 7px buffer (without the 7px the last divider could be cut off intermittently)
    tabContentContainerPadding + chevronWidth + 7,
    reactMLRIsEnabled
  );

  // Initialize the virtualScalingState and fill it with our known widths
  let draftVirtualScalingState = initializeVirtualScalingStateSLR(scalingState, knownWidths);
  lastKnownWidths = { ...knownWidths };

  // Determine the effect of applying each scaling step
  estimatedWidths = estimateRibbonScalingStepsSLR(scalingState, scalingData.width, draftVirtualScalingState);
}

/**
 * Add the provided groupId to controlGroupsWIthOverflowingContent if it is the first instance
 * of a control in this group overflowing
 * @param groupId The group id where an overflow is happening
 * @returns True this was the first instance of a control overflowing in the given group
 */
const addGroupWithOverflowIfFirstInstance = (groupId: string) => {
  if (controlGroupsWithOverflowingContent.includes(groupId)) {
    return false;
  }
  controlGroupsWithOverflowingContent.push(groupId);
  return true;
};

/**
 * Checks if a scaling step contains a *ToOverflow step
 * @param scalingStep The scaling step to check for a moveToOverflow step
 * @returns True if a *ToOverflow step was found, false otherwise
 */
export function stepContainsToOverflow(
  scalingStep: RibbonScalingTransformation | RibbonScalingTransformation[] | undefined
): boolean {
  if (Array.isArray(scalingStep)) {
    return scalingStep.some(step => stepContainsToOverflow(step));
  }

  return !!scalingStep && (scalingStep.type === 'MoveToOverflow' || scalingStep.type === 'MoveGroupToSingleOverflow');
}

/**
 * Gets all the *ToOverflow scaling steps inside of the given scaling step
 * @param scalingStep The scaling step to get all *toOverflow scaling steps
 * @returns An array of all *ToOverflow scaling steps
 */
export function getToOverflowSteps(
  scalingStep: RibbonScalingTransformation | RibbonScalingTransformation[] | undefined
): RibbonScalingTransformation[] {
  let result: RibbonScalingTransformation[] = [];
  if (Array.isArray(scalingStep)) {
    scalingStep.forEach(step => (result = result.concat(getToOverflowSteps(step))));
    return result;
  }

  return !!scalingStep && stepContainsToOverflow(scalingStep) ? [scalingStep] : [];
}

/**
 * Returns an array for the scaling step at the given scaling index
 * @param scalingIndex The scaling index to access in the scaling step
 * @param scalingSteps The scaling steps
 * @returns an array consisting of the scaling step at the given scaling index
 */
function getScalingStepsAtIndexAsArray(
  scalingIndex: number,
  scalingSteps: Readonly<RibbonScalingStep>[]
): RibbonScalingTransformation[] {
  let step = scalingSteps[scalingIndex];
  return Array.isArray(step) ? step : [step];
}

/**
 * This function will populate the overflow for the given groupId
 * with the correct MenuDefinition once clicked
 * @param groupId The groupId to build the overflow menu definition for
 * @returns A menu definition based off of the last know scaling state and window width,
 * or undefined if the menu definition cannot be determined
 */
export function getCurrentOverflowMenuDefinition(
  groupId: string,
  overflowMenuDefinition?: MenuDefinition
): MenuDefinition | undefined {
  if (!lastKnownScalingState || !overflowMenuDefinition) {
    return undefined;
  }

  let scalingStepToApply = lastKnownScalingState.minimumScalingStepsToApply || 0;
  let spaceAvailable = window.innerWidth;

  // If the full contents do not fit, find the right scaling step
  if (spaceAvailable < estimatedWidths[scalingStepToApply]) {
    let step = undefined;
    for (step in estimatedWidths) {
      if (estimatedWidths[step] < spaceAvailable) {
        scalingStepToApply = +step;
        break;
      }
    }

    // If we get here and we haven't changed stepsToApply, we walked off the end of the scaling steps
    // and the space available is smaller than all the scaling steps, choose the last one
    if (step && (scalingStepToApply === lastKnownScalingState.minimumScalingStepsToApply || 0)) {
      scalingStepToApply = lastKnownScalingState.scalingSteps.length;
    }
  }

  const allOverflowControlIds: string[] = [];

  // Compile all of the controls that are in overflows up to the given scaling step
  buildOverflowControlIdArray(lastKnownScalingState.scalingSteps.slice(0, scalingStepToApply), allOverflowControlIds);
  const shouldRenderSingleOverflowMenu = !!lastKnownScalingState.scaledEndOfRibbonOverflowMenu;

  // Check if we are dealing with the single overflow button
  if (groupId === singleRibbonOverflowMenuId) {
    // If we are told to get a single overflow definition but should not
    // be rendering a single overflow menu, shouldRenderSingleOverflowMenu wins
    if (!shouldRenderSingleOverflowMenu) {
      return undefined;
    }

    return getSingleOverflowMenuDefinition(overflowMenuDefinition, allOverflowControlIds);
  }

  // If we get here, we are a typical group overflow (not the single overflow).
  // Attempt to find the group
  const group = lastKnownScalingState.ribbonGroupDefinitions.groups.find(group => group.id === groupId);

  // If we were not able to find the group or it should not have an overflow, return
  if (!group || (!group.hasOwnOverflow && shouldRenderSingleOverflowMenu)) {
    return undefined;
  }

  // Attempt to find the section for this control group in the menu definition
  const section = overflowMenuDefinition.sections.find(
    (section: MenuSectionGeneric<SimpleMenuItemControlProps>) =>
      (section.controls[0] as WithSectionId<SimpleMenuItemControlProps>)?.sectionId === groupId
  );

  // If we are not able to find a matching section in the menu definition, return undefined
  if (!section) {
    return undefined;
  }

  overflowMenuDefinition.sections = getGroupOverflowMenuDefinition(section, allOverflowControlIds);
  return overflowMenuDefinition;
}

/**
 * Gets a correctly scoped menu definition for the control group overflow
 * @param section The menu section definition for this control group overflow
 * @param allOverflowControlIds An array of control id for all controls that are in overflows
 * @returns An updated menu definition for the groups overflow
 */
function getGroupOverflowMenuDefinition(
  section: MenuSectionGeneric<SimpleMenuItemControlProps>,
  allOverflowControlIds: string[]
) {
  const filteredSection: MenuSectionGeneric<SimpleMenuItemControlProps> = { controls: [] };
  filteredSection.controls = section.controls.filter((control: SimpleMenuItemControlProps) =>
    allOverflowControlIds.some(controlId => control.id === controlId)
  );
  return [filteredSection];
}

/**
 * Gets a correctly scoped menu definition for the single overflow
 * @param overflowMenuDefinition The single overflow menu definition
 * @param allOverflowControlIds An array of control id for all controls that are in overflows
 * @returns An updated menu definition for the single overflow
 */
function getSingleOverflowMenuDefinition(overflowMenuDefinition: MenuDefinition, allOverflowControlIds: string[]) {
  if (!lastKnownScalingState) {
    return undefined;
  }

  let menuDefinitionForOverflow: MenuDefinition = { sections: [] };

  // Get the sections that using the single overflow
  lastKnownScalingState.ribbonGroupDefinitions.groups.map(group => {
    if (!group.hasOwnOverflow || allOverflowControlIds.includes(group.id)) {
      const singleOverflowSection = overflowMenuDefinition.sections.find(
        (section: MenuSectionGeneric<SimpleMenuItemControlProps>) => section.id === group.id
      );
      singleOverflowSection && menuDefinitionForOverflow.sections.push(singleOverflowSection);
    }
  });

  // Iterate across the groups using single overflow and update the menu definition
  // consisting the sections/controls in each group's overflow
  menuDefinitionForOverflow.sections = menuDefinitionForOverflow.sections.filter(
    (section: MenuSectionGeneric<SimpleMenuItemControlProps>) => {
      const controlsForOverflow: SimpleMenuItemControlProps[] = section.controls.filter(
        (control: SimpleMenuItemControlProps) => allOverflowControlIds.find(controlId => control.id === controlId)
      );

      if (controlsForOverflow.length > 0) {
        section.controls = controlsForOverflow;
        return true;
      }

      // If we have no controls for the overflow then return false to remove
      // the section from the menu definition
      return false;
    }
  );

  return menuDefinitionForOverflow;
}

/**
 * Adds the overflowing control ids for the given scaling step to the given overflowControlIds array
 * @param scalingStep The scaling step to build an array of controls that are overflowing
 * @param overflowControlIds The array to add the overflowing control ids
 */
function buildOverflowControlIdArray(
  scalingStep: Readonly<RibbonScalingStep> | Readonly<RibbonScalingStep>[],
  overflowControlIds: string[]
) {
  if (Array.isArray(scalingStep)) {
    scalingStep.map(step => buildOverflowControlIdArray(step, overflowControlIds));
    return;
  }

  // Add the control or group id if the scaling step's type says that it is moved to an overflow
  if (stepContainsToOverflow(scalingStep as RibbonScalingTransformation)) {
    overflowControlIds.push(
      (scalingStep as MoveToOverflow).controlId || (scalingStep as MoveGroupToSingleOverflow).groupId
    );
  }
}
